import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query'
import { useMemo } from 'react'
import { useApi } from '@/hooks/use-api'
import { parseApiDate } from '@/lib/dates'

export type ConversationStatus = 'active' | 'replied' | 'escalated' | 'paused'
export type InboxFilter = 'all' | 'needs_attention' | 'escalated' | 'replied'
/**
 * Source scope, orthogonal to the status filter: 'campaign' = conversations
 * DealCloser drove (campaign leads + engagement DMs), 'direct' = organic
 * LinkedIn conversations DealCloser never touched, 'all' = both.
 */
export type InboxScope = 'all' | 'campaign' | 'direct'

export interface Conversation {
  id: string
  lead_id: string
  lead_name: string
  lead_company: string
  lead_avatar?: string
  status: ConversationStatus
  last_message: string
  last_message_at: string
  unread: boolean
  flag?: string
  /**
   * ISO timestamp set when the user first manually replies to this lead
   * from the inbox. When set, AI auto-reply is permanently disabled for
   * this lead. Used to render the "AI auto-reply paused" banner.
   */
  manually_handled_at?: string | null
  /** Which side drove this conversation — see InboxScope. Absent on cached
   *  pre-rollout payloads, treated as 'campaign' by the scope fallback. */
  origin?: 'campaign' | 'direct'
  /**
   * Waiting on a human (server rule, 2026-09-18): the other person spoke
   * last, recently, with something worth answering, and the AI will not
   * answer it on its own. Drives the Attention tab.
   */
  needs_attention?: boolean
  attention_reason?: AttentionReason
  /** Status circle color and line icons (workers inbox-stage.ts). Absent on
   *  cached pre-rollout payloads: the circle falls back to `status`. */
  stage?: InboxStage
  signals?: InboxSignal[]
}

export type InboxStage = 'attention' | 'booked' | 'not_interested' | 'hot' | 'interested' | 'replied' | 'no_reply'

export type InboxSignal =
  | 'attention'
  | 'sale'
  | 'booked'
  | 'meeting_proposed'
  | 'link_sent'
  | 'follow_up'
  | 'cold'
  | 'not_interested'

export type AttentionReason = 'escalated' | 'unanswered' | 'ai_stuck'

export const ATTENTION_REASON_LABELS: Record<AttentionReason, string> = {
  escalated: 'AI handed this to you',
  unanswered: 'Waiting on your reply',
  ai_stuck: "AI hasn't replied",
}

export type MessageType =
  | 'connection_request'
  | 'first_dm'
  | 'follow_up_1'
  | 'follow_up_2'
  | 'ai_reply'
  // The lead's own message. Distinct from 'ai_reply' (our AI's answer).
  | 'reply'
  // Outbound whose funnel step isn't known (e.g. sent outside DealCloser).
  | 'message'

export type MessageStatus = 'sent' | 'delivered' | 'read' | 'failed' | 'pending'

export interface Message {
  id: string
  lead_id: string
  direction: 'outbound' | 'inbound'
  type: MessageType
  content: string
  sent_at: string
  status: MessageStatus
  is_ai_generated: boolean
  editable: boolean
  sentiment?: string
  classification?: string
}

// Client-side filter fallback (used if backend doesn't support filter param)
function filterConversations(
  conversations: Conversation[],
  filter: InboxFilter,
): Conversation[] {
  switch (filter) {
    case 'needs_attention':
      // Server decides (inbox-attention.ts). The old `unread || active`
      // rule matched every conversation that was not a replied lead.
      return conversations.filter((c) => c.needs_attention === true)
    case 'escalated':
      return conversations.filter((c) => c.status === 'escalated')
    case 'replied':
      return conversations.filter((c) => c.status === 'replied')
    case 'all':
    default:
      return conversations
  }
}

// Client-side scope fallback, mirroring the backend's applyScope. A
// conversation without `origin` (stale KV cache from before the rollout)
// counts as 'campaign' so DCA rows never vanish from their own tab.
function scopeConversations(
  conversations: Conversation[],
  scope: InboxScope,
): Conversation[] {
  switch (scope) {
    case 'campaign':
      return conversations.filter((c) => (c.origin ?? 'campaign') === 'campaign')
    case 'direct':
      return conversations.filter((c) => c.origin === 'direct')
    case 'all':
    default:
      return conversations
  }
}

/** One page of the conversation list (server shape for ?limit=&offset=). */
export interface ConversationPage {
  items: Conversation[]
  total: number
  offset: number
  limit: number
  has_more: boolean
  next_offset: number | null
  /** Server is still filling in attendee names in the background — poll briefly. */
  partial?: boolean
}

export const CONVERSATION_PAGE_SIZE = 30

/**
 * Lazy-loaded conversation list (2026-09-07). The first page renders as
 * soon as it arrives; further pages load as the list scrolls
 * (see ConversationList's sentinel). Switching filter/scope keeps the
 * previous list on screen instead of flashing skeletons, and a `partial`
 * page (server still warming attendee names) is re-polled every few
 * seconds until it settles.
 */
export function useConversations(filter: InboxFilter = 'all', scope: InboxScope = 'all') {
  const apiFetch = useApi()

  const query = useInfiniteQuery({
    queryKey: ['conversations', filter, scope],
    initialPageParam: 0,
    queryFn: async ({ pageParam }): Promise<ConversationPage> => {
      const params = new URLSearchParams()
      if (filter !== 'all') {
        params.set('filter', filter)
      }
      if (scope !== 'all') {
        params.set('scope', scope)
      }
      params.set('limit', String(CONVERSATION_PAGE_SIZE))
      params.set('offset', String(pageParam))
      const res = await apiFetch<ConversationPage>(`/api/inbox/conversations?${params.toString()}`)
      if (!res.success || !res.data)
        throw new Error(res.error ?? 'Failed to load conversations')
      // Apply client-side filter/scope as fallback in case backend ignores the params
      return {
        ...res.data,
        items: scopeConversations(filterConversations(res.data.items ?? [], filter), scope),
      }
    },
    getNextPageParam: (last) =>
      last.has_more && last.next_offset !== null ? last.next_offset : undefined,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    refetchInterval: (q) => (q.state.data?.pages.some((p) => p.partial) ? 4_000 : false),
  })

  const conversations = useMemo(
    () => query.data?.pages.flatMap((p) => p.items) ?? [],
    [query.data],
  )
  const lastPage = query.data?.pages[query.data.pages.length - 1]
  const total = lastPage?.total ?? conversations.length

  return {
    conversations,
    total,
    isLoading: query.isPending,
    isError: query.isError,
    error: query.error,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    isPlaceholderData: query.isPlaceholderData,
    refetch: query.refetch,
  }
}

export function useConversation(leadId: string | null) {
  const apiFetch = useApi()

  return useQuery({
    queryKey: ['conversation', leadId],
    queryFn: async (): Promise<Message[]> => {
      if (!leadId) return []
      const res = await apiFetch<Message[]>(`/api/inbox/conversations/${leadId}/messages`)
      if (!res.success || !res.data)
        throw new Error(res.error ?? `Failed to load messages for lead ${leadId}`)
      return res.data
    },
    enabled: leadId !== null,
    staleTime: 30_000,
  })
}

interface EditMessagePayload {
  messageId: string
  leadId: string
  newContent: string
}

export function useEditMessage() {
  const queryClient = useQueryClient()
  const apiFetch = useApi()

  return useMutation({
    mutationFn: async (payload: EditMessagePayload): Promise<Message> => {
      const res = await apiFetch<Message>(`/api/inbox/messages/${payload.messageId}`, {
        method: 'PUT',
        body: { content: payload.newContent },
      })
      if (!res.success || !res.data)
        throw new Error(res.error ?? `Failed to edit message ${payload.messageId}`)
      return res.data
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['conversation', variables.leadId] })
    },
  })
}

export function useSendReply() {
  const queryClient = useQueryClient()
  const apiFetch = useApi()

  return useMutation({
    mutationFn: async ({ leadId, content }: { leadId: string; content: string }): Promise<Message> => {
      const res = await apiFetch<Message>(`/api/inbox/conversations/${leadId}/reply`, {
        method: 'POST',
        body: { content },
      })
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to send reply')
      return res.data
    },
    onSuccess: (_data, { leadId }) => {
      void queryClient.invalidateQueries({ queryKey: ['conversation', leadId] })
      void queryClient.invalidateQueries({ queryKey: ['conversations'] })
    },
  })
}

// ---------------------------------------------------------------------------
// Review queue (manual message approval)
// ---------------------------------------------------------------------------

export interface ApprovalItem {
  id: string
  lead_id: string
  lead_name: string
  lead_company: string | null
  lead_title: string | null
  campaign_name: string | null
  message_type: string
  content: string
  /** 'review' = held by your review mode; 'stuck' = an AI reply that needs a manual push. */
  hold_reason: 'review' | 'stuck'
  qg_score: number | null
  qg_feedback: string | null
  created_at: string
}

export function useApprovals() {
  const apiFetch = useApi()
  return useQuery({
    queryKey: ['approvals'],
    queryFn: async (): Promise<ApprovalItem[]> => {
      const res = await apiFetch<ApprovalItem[]>('/api/inbox/approvals')
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load review queue')
      return res.data
    },
    refetchInterval: 30_000,
    staleTime: 15_000,
  })
}

export function useApproveMessage() {
  const queryClient = useQueryClient()
  const apiFetch = useApi()
  return useMutation({
    mutationFn: async ({ id, content }: { id: string; content?: string }): Promise<void> => {
      const res = await apiFetch(`/api/inbox/approvals/${id}/approve`, {
        method: 'POST',
        body: content !== undefined ? { content } : {},
      })
      if (!res.success) throw new Error(res.error ?? 'Failed to send the message')
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['approvals'] })
      void queryClient.invalidateQueries({ queryKey: ['conversations'] })
      void queryClient.invalidateQueries({ queryKey: ['conversation'] })
    },
  })
}

export function useReviseMessage() {
  const queryClient = useQueryClient()
  const apiFetch = useApi()
  return useMutation({
    mutationFn: async ({ id, feedback }: { id: string; feedback: string }): Promise<string> => {
      const res = await apiFetch<{ id: string; content: string }>(
        `/api/inbox/approvals/${id}/revise`,
        { method: 'POST', body: { feedback } },
      )
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to revise the draft')
      return res.data.content
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['approvals'] })
    },
  })
}

export function useRejectMessage() {
  const queryClient = useQueryClient()
  const apiFetch = useApi()
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const res = await apiFetch(`/api/inbox/approvals/${id}/reject`, { method: 'POST', body: {} })
      if (!res.success) throw new Error(res.error ?? 'Failed to discard the draft')
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['approvals'] })
    },
  })
}

export function useMarkAsRead() {
  const apiFetch = useApi()

  return useMutation({
    mutationFn: async (leadId: string): Promise<void> => {
      await apiFetch(`/api/leads/${leadId}`, {
        method: 'PUT',
        body: { last_activity_at: new Date().toISOString() },
      })
    },
  })
}

export function getMessageTypeLabel(type: MessageType): string {
  const labels: Record<MessageType, string> = {
    connection_request: 'Connection Request',
    first_dm: 'First DM',
    follow_up_1: 'Follow-up 1',
    follow_up_2: 'Follow-up 2',
    ai_reply: 'AI Reply',
    reply: 'Reply',
    message: 'Message',
  }
  return labels[type]
}

export function formatRelativeTime(isoString: string): string {
  // parseApiDate handles the DB's mixed ISO/space timestamp formats —
  // the space form is UTC but parses as local time (or Invalid Date on
  // Safari) if handed to new Date() raw.
  const date = parseApiDate(isoString)
  if (!date) return '—'
  const diff = Date.now() - date.getTime()
  const minutes = Math.floor(diff / 60_000)
  const hours = Math.floor(diff / 3_600_000)
  const days = Math.floor(diff / 86_400_000)

  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  if (hours < 24) return `${hours}h ago`
  if (days < 7) return `${days}d ago`
  return date.toLocaleDateString()
}
